// Language switcher — flips the active locale and keeps the current path
// stable. Renders a compact native <select> for accessibility + keyboard
// navigation; a small Globe icon hints at language switching.

import { useLocation } from "wouter";
import { Globe } from "lucide-react";
import {
  LOCALES,
  LOCALE_LABELS,
  LOCALE_PREF_KEY,
  swapLocaleInPath,
  type Locale,
} from "@/lib/locale";
import { useLocale } from "@/lib/locale-context";

interface LanguageSwitcherProps {
  className?: string;
  compact?: boolean;
}

export function LanguageSwitcher({ className, compact }: LanguageSwitcherProps) {
  const { locale } = useLocale();
  const [, setLocation] = useLocation();

  const onChange = (next: string) => {
    if (!LOCALES.includes(next as Locale)) return;
    if (typeof window === "undefined") return;
    // Persist the manual choice so the auto-redirect never overrides it.
    try {
      localStorage.setItem(LOCALE_PREF_KEY, next);
    } catch {
      // Private browsing or storage quota — ignore.
    }
    // wouter's `useLocation` here gives us the path *inside* the configured
    // base. We compute the swap on `window.location.pathname` so the result
    // is always an absolute root-anchored URL — then strip the base prefix
    // back off so wouter picks it up. Doing this in absolute space avoids
    // edge cases where the SPA was mounted at a sub-path.
    const baseUrl = (import.meta.env.BASE_URL ?? "/").replace(/\/$/, "");
    const absPath = window.location.pathname || "/";
    const swapped = swapLocaleInPath(absPath, next as Locale);
    const target = baseUrl && swapped.startsWith(baseUrl)
      ? swapped.slice(baseUrl.length) || "/"
      : swapped;
    // Use full navigation (location.assign) rather than wouter's setLocation
    // so prerendered locale shells are loaded freshly. SPA hydration in the
    // new locale is correct, but the prerendered HTML for the target locale
    // is what we want to serve to crawlers and on first paint.
    if (typeof window !== "undefined") {
      window.location.assign(swapped);
    } else {
      setLocation(target);
    }
  };

  return (
    <label
      className={
        "inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer " +
        (className ?? "")
      }
      data-testid={compact ? "language-switcher-compact" : "language-switcher"}
    >
      <Globe className="w-4 h-4" />
      {!compact && <span className="sr-only">Language</span>}
      <select
        value={locale}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent border-none outline-none cursor-pointer text-sm font-medium uppercase tracking-wider focus:ring-0"
        aria-label="Language"
        data-testid={compact ? "language-switcher-select-compact" : "language-switcher-select"}
      >
        {LOCALES.map((l) => (
          <option key={l} value={l}>
            {compact ? l.toUpperCase() : LOCALE_LABELS[l]}
          </option>
        ))}
      </select>
    </label>
  );
}
